import type { QueryClient } from '@tanstack/react-query';
import { axiosInstance, isOutboxMultipartBody, type OutboxRequest } from '@kidshome/api-contract';
import {
  bumpAttempts,
  countOutboxFor,
  enqueueOutbox,
  listOutbox,
  removeOutbox,
  type OutboxEntry,
} from './outbox-store';

/**
 * The offline-outbox controller (ADR-0018, Phase 7). It owns the queue lifecycle the UI cares about:
 *   - `enqueue` — called by the api-contract outbox bridge when a write fails offline.
 *   - `replay` — drains the queue FIFO via the raw `axiosInstance` (re-sending the stored `Idempotency-Key`), so
 *     the server (which dedupes on that key) treats a replayed "claim coins"/redemption as a no-op.
 *   - state (`pending` count, `online`, last permanent failure) exposed to React via a subscribable
 *     snapshot for `useSyncExternalStore`.
 *
 * Replay outcome per entry:
 *   - success → remove + invalidate the affected queries (server is authoritative; reconcile).
 *   - permanent 4xx (validation/conflict/forbidden, NOT 401/408/429 and NOT the idempotency
 *     in-progress 409 — see `replayOne`) → remove + surface to the user (never silently dropped;
 *     EVERY failure is kept on the snapshot, not just the latest).
 *   - auth 401 → left queued WITHOUT spending the retry budget (no attempt bump, never surfaced); it
 *     replays after re-auth. A brief auth outage re-foregrounded a few times must not discard valid work.
 *   - transient (offline/5xx/429/network) → keep, bump attempts, stop the drain (retry next trigger).
 * We never infinite-retry: a permanent failure is removed immediately; a transient one waits for the
 * next online/foreground trigger rather than spinning, AND is capped at `MAX_REPLAY_ATTEMPTS` — a
 * genuinely stuck entry (a persistent 5xx, a 429 that never clears) is then surfaced + dropped instead
 * of re-sending forever on every trigger.
 *
 * SHARED-DEVICE OWNERSHIP: every entry is stamped with the enqueuing member's id (the `owner` getter
 * wired via `attach`). Replay only ever sends entries whose owner IS the currently signed-in member —
 * another member's entries stay queued (and drain when their owner signs back in), so kid A's offline
 * chore/redemption can never be applied under kid B's session after a logout/login on the family
 * tablet. The pending badge is likewise scoped to the current member.
 */

export interface OutboxFailure {
  readonly idempotencyKey: string;
  readonly url: string;
  readonly status: number | null;
}

export interface OutboxSnapshot {
  /** Number of mutations still waiting to sync (scoped to the signed-in member). */
  readonly pending: number;
  /** Browser connectivity (mirrors `navigator.onLine`). */
  readonly online: boolean;
  /** True while a drain is in progress. */
  readonly syncing: boolean;
  /**
   * Every permanently-failed entry not yet acknowledged by the user (a single drain can reject more
   * than one queued mutation; ALL of them are surfaced, never just the latest). The UI clears them
   * one-by-one (or all) via `clearFailure`.
   */
  readonly failures: readonly OutboxFailure[];
}

type Listener = () => void;

/**
 * Max transient replay attempts before we give up on an entry and surface it as a failure. A genuinely
 * stuck mutation (a persistent 5xx, a 429 that never clears) must NOT re-send forever on every online/
 * foreground trigger — after this many tries we drop it and tell the user it couldn't sync.
 */
const MAX_REPLAY_ATTEMPTS = 8;

/** A 4xx that won't fix itself on replay → surface + drop. 401/408/429 are transient (retry later). */
function isPermanentClientError(status: number | undefined): boolean {
  if (status === undefined) return false;
  if (status === 401 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}

function statusOf(error: unknown): number | null {
  const s = (error as { response?: { status?: number }; status?: number })?.response?.status;
  return s ?? (error as { status?: number })?.status ?? null;
}

/** The stable machine-readable `code` from an RFC 9457 problem+json error body, when present. */
function codeOf(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * Rebuild the replay body from what the outbox persisted: a serialized FormData (the mutator stores
 * its cloneable `[name, value]` entries — IndexedDB can't clone FormData itself) is reconstructed
 * into a real FormData; anything else replays as-is (JSON).
 */
function toReplayBody(body: unknown): { data: unknown; multipart: boolean } {
  if (isOutboxMultipartBody(body)) {
    const form = new FormData();
    for (const [name, value] of body.__multipart) {
      if (typeof value === 'string') {
        form.append(name, value);
      } else {
        // A file part is persisted as raw bytes + metadata — rebuild the File it stood for.
        form.append(
          name,
          new File([value.__file.bytes], value.__file.name, { type: value.__file.type }),
        );
      }
    }
    return { data: form, multipart: true };
  }
  return { data: body, multipart: false };
}

export class OutboxController {
  private qc: QueryClient | null = null;
  private listeners = new Set<Listener>();
  private snapshot: OutboxSnapshot = {
    pending: 0,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    syncing: false,
    failures: [],
  };
  private draining = false;
  /** Pending coalesced-replay timer (see {@link scheduleReplay}); null when none is scheduled. */
  private replayTimer: ReturnType<typeof setTimeout> | null = null;
  /** Maps a queued URL path to the query keys to invalidate after a successful replay. */
  private invalidate: (entry: OutboxEntry) => Array<readonly unknown[]> = () => [];
  /** The signed-in member's id (null when signed out). Stamps enqueues + gates replays. */
  private owner: () => string | null = () => null;
  /**
   * The LIVE platform-owner impersonation scope (ADR-0055) — the household currently being acted in,
   * or null when not impersonating. Replay gates on it so a write enqueued in one household's scope is
   * never sent while the owner is acting in a DIFFERENT household (mirrors the `owner` gate). Defaults
   * to "never impersonating" for callers that don't impersonate (tests, non-owner builds).
   */
  private actingAs: () => string | null = () => null;

  /**
   * Wire the controller to the app's QueryClient, an invalidation mapper, the current-member getter
   * (the shared-device ownership gate), and the live acting-as scope getter (the impersonation gate).
   * Idempotent.
   */
  attach(
    qc: QueryClient,
    invalidate: (entry: OutboxEntry) => Array<readonly unknown[]>,
    owner: () => string | null,
    actingAs: () => string | null = () => null,
  ): void {
    this.qc = qc;
    this.invalidate = invalidate;
    this.owner = owner;
    this.actingAs = actingAs;
    void this.refreshCount();
  }

  /**
   * The signed-in member changed (login/logout/switch on a shared device). Re-scope the pending
   * badge and drain anything the RETURNING member queued earlier (their entries waited while someone
   * else — or no one — was signed in).
   */
  ownerChanged(): void {
    // Surfaced failures aren't owner-stamped, so a blanket reset on owner change is correct — kid B
    // must never see kid A's failed-mutation alerts after a switch on the shared device.
    this.setSnapshot({ failures: [] });
    void this.refreshCount();
    void this.replay();
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): OutboxSnapshot => this.snapshot;

  private setSnapshot(patch: Partial<OutboxSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const l of this.listeners) l();
  }

  /** The api-contract outbox bridge calls this. Persists + updates the badge; returns accepted. */
  enqueue = async (request: OutboxRequest): Promise<boolean> => {
    // Stamp the enqueuing member — only a session-holder can issue a write, so the owner is the
    // member whose action this is, and the ONLY member it may ever replay under.
    await enqueueOutbox(request, this.owner());
    await this.refreshCount();
    return true;
  };

  setOnline(online: boolean): void {
    this.setSnapshot({ online });
    if (online) this.scheduleReplay();
  }

  /**
   * Coalesce replay triggers. Re-foregrounding fires focus + pageshow + visibilitychange + online in
   * one synchronous burst; without this each would kick its own drain, multiplying transient retries
   * during a brief outage and burning an entry's `MAX_REPLAY_ATTEMPTS` budget. A short (next-tick)
   * timer folds all triggers in the same burst into AT MOST ONE `replay()`.
   */
  scheduleReplay = (): void => {
    if (this.replayTimer !== null) return; // already scheduled for this burst — fold into it
    this.replayTimer = setTimeout(() => {
      this.replayTimer = null;
      void this.replay();
    }, 0);
  };

  /** Acknowledge one surfaced failure (by idempotency key), or all of them when no key is given. */
  clearFailure(idempotencyKey?: string): void {
    if (this.snapshot.failures.length === 0) return;
    const failures =
      idempotencyKey === undefined
        ? []
        : this.snapshot.failures.filter((f) => f.idempotencyKey !== idempotencyKey);
    if (failures.length !== this.snapshot.failures.length) this.setSnapshot({ failures });
  }

  private async refreshCount(): Promise<void> {
    const pending = await countOutboxFor(this.owner());
    if (pending !== this.snapshot.pending) this.setSnapshot({ pending });
  }

  /**
   * Drain the queue FIFO — ONLY the entries owned by the currently signed-in member. Another
   * member's entries are skipped (they stay queued for when that member signs back in); entries with
   * no owner never replay. Re-entrancy guarded (`draining`) so overlapping triggers (online +
   * visibility firing together) don't double-send. Stops at the first transient failure to avoid a
   * hot loop while still offline; resumes on the next trigger.
   */
  replay = async (): Promise<void> => {
    if (this.draining) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    const owner = this.owner();
    if (owner === null) return; // signed out — nothing may be sent under no/another session
    this.draining = true;
    this.setSnapshot({ syncing: true });
    try {
      const entries = await listOutbox();
      for (const entry of entries) {
        // Re-read the LIVE signed-in member per entry (not once before the loop): a mid-drain member switch
        // on a shared device (logout→login) no-ops scheduleReplay against this in-flight drain, so a stale
        // owner would replay the previous member's queued write under the NEW member's live access token —
        // a cross-account write to the wrong child's ledger. Signed out mid-drain ⇒ stop entirely.
        const liveOwner = this.owner();
        if (liveOwner === null) break;
        if (entry.ownerId !== liveOwner) continue; // someone else's work — leave it for their session
        // Impersonation scope gate (ADR-0055): a write enqueued in one household's acting-as scope must
        // not be sent while the owner is acting in a DIFFERENT one (or not acting at all) — that would
        // land the write in the wrong household. Leave it queued for when the owner re-enters its scope.
        // Re-read the LIVE scope per entry (not once before the loop): a mid-drain acting-as switch's
        // scheduleReplay() no-ops against this in-flight drain, so a stale snapshot would let an entry
        // through under the wrong scope.
        const liveActingAs = this.actingAs();
        if ((entry.actAsHouseholdId ?? null) !== liveActingAs) continue;
        const outcome = await this.replayOne(entry);
        if (outcome === 'transient') break; // wait for the next online/foreground trigger
      }
    } finally {
      // Clear the syncing flag BEFORE the awaited count: refreshCount()'s unguarded IndexedDB read can
      // reject (iOS storage eviction / connection loss), which would otherwise skip setSnapshot and leave
      // the "Syncing…" pill stuck on forever.
      this.draining = false;
      this.setSnapshot({ syncing: false });
      await this.refreshCount();
    }
  };

  private async replayOne(entry: OutboxEntry): Promise<'done' | 'failed' | 'transient'> {
    try {
      // Re-issue through the raw axios instance (NOT `apiFetch`), re-attaching the stored idempotency
      // key so the server dedupes the replay. We deliberately bypass the `apiFetch` mutator because its
      // catch block still has the offline-outbox bridge installed: a network error mid-replay (offline,
      // or an `ERR_NETWORK`/timeout while online) would be re-enqueued and RESOLVED as `undefined`
      // instead of rejecting — `removeOutbox` below would then delete the entry on a fake success and
      // permanently DISCARD the queued write. Going through `axiosInstance` directly keeps the auth/CSRF
      // request interceptor and the 401→refresh→retry response interceptor, but lets a real network
      // failure REJECT so it falls into the transient branch and the entry survives for the next drain.
      const { data, multipart } = toReplayBody(entry.body);
      await axiosInstance({
        url: entry.url,
        method: entry.method,
        headers: {
          // A multipart replay must NOT pin `application/json` — with no manual Content-Type,
          // axios/the browser computes the multipart header WITH its boundary from the FormData.
          ...(multipart ? {} : { 'Content-Type': 'application/json' }),
          'Idempotency-Key': entry.idempotencyKey,
          // Re-attach the impersonation scope (ADR-0055) snapshotted at enqueue so the replay lands in
          // the household this write was authored in — NOT whatever scope the request interceptor would
          // attach from the live store. Only sent when this write was actually authored under one.
          ...(entry.actAsHouseholdId !== null
            ? { 'X-Act-As-Household': entry.actAsHouseholdId }
            : {}),
        },
        ...(data !== undefined ? { data } : {}),
      });
      await removeOutbox(entry.id);
      this.invalidateFor(entry);
      return 'done';
    } catch (error) {
      const status = statusOf(error);
      // The idempotency interceptor's concurrent-claim 409 (IDEMPOTENCY_IN_PROGRESS) is TRANSIENT,
      // not key reuse: a sibling window over the SAME IndexedDB store (browser tab + installed PWA
      // both firing 'online') — or a slow earlier retry still in flight — holds the claim for this
      // very entry right now. Treat it like any other retryable failure: the next drain replays the
      // server's cached response (or the sibling already removed the entry), and the attempts cap
      // still bounds a genuinely stuck one. Domain 409s (OVER_BUDGET, …) stay permanent.
      const concurrentClaim = status === 409 && codeOf(error) === 'IDEMPOTENCY_IN_PROGRESS';
      if (!concurrentClaim && isPermanentClientError(status ?? undefined)) {
        // Won't succeed on replay — drop it and surface to the user (never silently lost).
        await this.surfaceFailure(entry, status);
        return 'failed';
      }
      // 401 = awaiting re-auth: NOT a retry budget spend. Leave it queued WITHOUT bumping attempts or
      // ever surfacing it — the entry replays after re-auth (the mutator refreshes transparently). A
      // brief auth outage re-foregrounded a few times must not burn the budget and discard valid work.
      if (status === 401) return 'transient';
      // Transient (offline / 5xx / 429 / network): keep it and retry on the next trigger — UNLESS it has
      // now exhausted its retry budget, in which case give up like a permanent failure (surface + drop)
      // so a permanently-stuck entry can't re-send forever or wedge the queue behind it.
      const attempts = await bumpAttempts(entry);
      if (attempts >= MAX_REPLAY_ATTEMPTS) {
        await this.surfaceFailure(entry, status);
        return 'failed';
      }
      return 'transient';
    }
  }

  /**
   * Drop a failed entry and surface it to the user (never silently lost). APPEND to the surfaced list
   * (deduped by key) so a drain that rejects several mutations shows every one of them.
   */
  private async surfaceFailure(entry: OutboxEntry, status: number | null): Promise<void> {
    await removeOutbox(entry.id);
    const already = this.snapshot.failures.some((f) => f.idempotencyKey === entry.idempotencyKey);
    if (!already) {
      this.setSnapshot({
        failures: [
          ...this.snapshot.failures,
          { idempotencyKey: entry.idempotencyKey, url: entry.url, status },
        ],
      });
    }
  }

  private invalidateFor(entry: OutboxEntry): void {
    if (!this.qc) return;
    for (const key of this.invalidate(entry)) {
      void this.qc.invalidateQueries({ queryKey: key });
    }
  }
}

/** App-singleton controller (one queue per tab). */
export const outboxController = new OutboxController();
